import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/Dialog';
import {
  useSignalRelatedIssues,
  useUpdateSignal,
  useApplySignal,
  useChangeSignalScope,
} from '@/shared/hooks/useSignals';
import { useTeams } from '@/shared/hooks/useTeams';
import type { Signal, ResolvedRelevantParty } from '@momentum/types';
import {
  SignalInstalledStatus,
  SignalVisibilityType,
  SignalApplicabilityScope,
} from '@momentum/types';
import type { EmployeeInfo } from '../constants';
import { SEVERITY_CONFIG } from './signal-detail/signal-detail.constants';
import { SignalDetailHeader } from './signal-detail/SignalDetailHeader';
import { SignalDetailContent } from './signal-detail/SignalDetailContent';
import { SignalDetailSidebar } from './signal-detail/SignalDetailSidebar';

type SignalWithOptionalCount = Signal & {
  relatedIssuesCount?: number;
  resolvedRelevantParties?: ResolvedRelevantParty[];
};

interface SignalDetailModalProps {
  signal: SignalWithOptionalCount | null;
  currentUserId: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (signal: Signal) => void;
  onToggleActive: (signal: Signal, isActive: boolean) => void;
  isTogglingActive: boolean;
  onRemove: (signal: Signal) => void;
  employeeMap: Map<string, EmployeeInfo>;
  isManager: boolean;
  directReportsCount: number;
  fullHierarchyCount: number;
}

export const SignalDetailModal = ({
  signal,
  currentUserId,
  isOpen,
  onClose,
  onDelete,
  onToggleActive,
  isTogglingActive,
  onRemove,
  employeeMap,
  isManager,
  directReportsCount,
  fullHierarchyCount,
}: SignalDetailModalProps) => {
  const { t } = useTranslation('knowledge');
  const updateSignal = useUpdateSignal();
  const applyMutation = useApplySignal();
  const changeScopeMutation = useChangeSignalScope();

  const { data: relatedIssues } = useSignalRelatedIssues(signal?._id, {
    enabled: isOpen && !!signal,
  });

  const { data: teams = [] } = useTeams(undefined, isOpen);

  const teamMap = useMemo(
    () => new Map(teams.map((team) => [team._id, team.name])),
    [teams]
  );

  const isActive = signal?.installed?.users?.some(
    (installedUser) =>
      installedUser.userId === currentUserId &&
      installedUser.status === SignalInstalledStatus.ACTIVE
  );

  const isPublic = signal?.visibility.type === SignalVisibilityType.PUBLIC;
  const isCreator = signal?.createdBy === currentUserId;

  const installedCount =
    signal?.installed?.users?.filter(
      (installedUser) => installedUser.status === SignalInstalledStatus.ACTIVE
    ).length ?? 0;

  const severityInfo = signal
    ? SEVERITY_CONFIG[signal.severity] ?? {
        labelKey: 'severityMedium',
        color: '#eab308',
      }
    : { labelKey: 'severityMedium', color: '#eab308' };

  const author = signal ? employeeMap.get(signal.createdBy) : undefined;

  const currentApplyOperation = signal?.applyOperations?.find(
    (op) => op.managerId === currentUserId
  );

  // Subordinate applied-by: find if current user was applied by someone else
  const installedUser = signal?.installed?.users?.find(
    (userEntry) => userEntry.userId === currentUserId
  );
  const appliedByName =
    installedUser?.appliedBy && installedUser.appliedBy !== currentUserId
      ? employeeMap.get(installedUser.appliedBy)?.fullName
      : undefined;

  const handleApplyScope = useCallback(
    async (scope: SignalApplicabilityScope) => {
      if (!signal) return;
      try {
        await applyMutation.mutateAsync({ id: signal._id, scope });
      } catch {
        toast.error(t('detailScopeApplyError'));
      }
    },
    [signal, applyMutation, t]
  );

  const handleChangeScope = useCallback(
    async (newScope: SignalApplicabilityScope) => {
      if (!signal) return;
      try {
        await changeScopeMutation.mutateAsync({
          id: signal._id,
          newScope,
        });
      } catch {
        toast.error(t('detailScopeUnapplyError'));
      }
    },
    [signal, changeScopeMutation, t]
  );

  const isApplying = applyMutation.isPending || changeScopeMutation.isPending;

  if (!signal) return null;

  const handleUpdate = (
    data: Parameters<typeof updateSignal.mutate>[0]['data']
  ): void => {
    updateSignal.mutate(
      { id: signal._id, data },
      {
        onError: () => toast.error(t('updateSignalError')),
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        size="lg"
        showCloseButton={false}
        className="flex flex-col gap-0 overflow-hidden p-0 !max-w-[1050px]"
      >
        <DialogTitle className="sr-only">{t('signalDetails')}</DialogTitle>

        <SignalDetailHeader
          isActive={!!isActive}
          isCreator={isCreator}
          isTogglingActive={isTogglingActive}
          onToggleActive={() => onToggleActive(signal, !!isActive)}
          onRemove={() => onRemove(signal)}
          onDelete={() => onDelete(signal)}
          onClose={onClose}
        />

        {/* Body -- two columns */}
        <div className="grid min-h-[480px] grid-cols-[1fr_280px] overflow-y-auto">
          <SignalDetailContent
            signal={signal}
            isActive={!!isActive}
            isCreator={isCreator}
            installedCount={installedCount}
            author={author}
            relatedIssues={relatedIssues}
            employeeMap={employeeMap}
            resolvedRelevantParties={signal.resolvedRelevantParties}
            isManager={isManager}
            currentApplyOperation={currentApplyOperation}
            directReportsCount={directReportsCount}
            fullHierarchyCount={fullHierarchyCount}
            onApplyScope={handleApplyScope}
            onChangeScope={handleChangeScope}
            isApplying={isApplying}
            appliedByName={appliedByName}
          />

          <SignalDetailSidebar
            signal={signal}
            severityInfo={severityInfo}
            author={author}
            employeeMap={employeeMap}
            teamMap={teamMap}
            isPublic={isPublic}
            resolvedRelevantParties={signal.resolvedRelevantParties}
            isCreator={isCreator}
            onUpdate={handleUpdate}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
